import React from 'react';
import cx from 'classnames';
import { capitalize, compact, groupBy, isNull, without } from 'lodash';
import { Link } from 'react-router-dom';

import {
  HighSignificanceIcon,
  LowSignificanceIcon,
  ModerateSignificanceIcon,
  SquareIcon
} from '@magoosh/lib/icons';
import ProgressBar from '@magoosh/lib/progress_bar';
import { paths } from 'config/path_helpers';
import { titleize } from 'utilities/formatting';
import { pluralize } from 'utilities/pluralize';

import StatisticData from '../statistic_data';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  filters: any;
}

const SectionConcepts: React.FC<Props> = (props) => {
  const iconForSection = (section) => {
    const colorPalette = { dark: colors.purple, light: colors.purpleLight };

    if (section === 'math') {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconType="math_operations"
          size={48}
        />
      );
    } else if (section === 'verbal') {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconType="notebook"
          size={48}
        />
      );
    } else if (section === 'writing') {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconType="pen"
          size={48}
        />
      );
    } else {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconContents={capitalize(section[0])}
          iconType="letter"
          size={48} />
      );
    }
  };

  return (
    <div className="d-flex flex-column gap-m">
      <div className="align-items-center d-flex gap-s">
        {iconForSection(props.filters.sections)}
        <h3 className="u-margin-A-n">{titleize(props.filters.sections)}</h3>
      </div>

      <div>
        <StatisticData
          filters={props.filters}
          name="performance_by_concept"
        >
          <StatisticData.Loading>
            <div style={{ minHeight: 150 }} />
          </StatisticData.Loading>
          {/* @ts-ignore */}
          <PerformanceByConcept period={props.filters.period} section={props.filters.sections} />
        </StatisticData>
      </div>
    </div>
  );
};

interface PerformanceByConceptProps {
  data: any; // TODO be specific about data format
  period: string;
  section: string;
}

const PerformanceByConcept: React.FC<PerformanceByConceptProps> = (props) => {
  const colorForGroup = (groupName) => {
    return {
      high: colors.dataDarkPurple,
      medium: colors.dataLightPurple,
      low: '#CB97FC',
      overall: colors.dataDarkPurple
    }[groupName];
  };

  const dataPointGroups = () => {
    const dataPoints = without(props.data, overallDataPoint());

    const conceptsByFrequency = groupBy(dataPoints, 'frequency');

    return compact([
      conceptsByFrequency.high && { frequency: 'high', concepts: conceptsByFrequency.high },
      conceptsByFrequency.medium && { frequency: 'medium', concepts: conceptsByFrequency.medium },
      conceptsByFrequency.low && { frequency: 'low', concepts: conceptsByFrequency.low }
    ]);
  };

  const overallDataPoint = () => {
    return props.data.find((datum) => datum.name === 'Overall');
  };

  const renderProgressBar = (groupName, dataPoint) => {
    const noAnswersOrNonCorrect = dataPoint.answers.total === 0 || dataPoint.answers.correct === 0 || isNull(dataPoint.answers.percentCorrect);
    const barColor = noAnswersOrNonCorrect ? colors.grayLighter : colorForGroup(groupName);
    const labelColor = noAnswersOrNonCorrect ? colors.grayLight : '#FFFFFF';
    const popOverText =
      dataPoint.answers.total === 0
        ? "You haven't answered any questions yet."
        : `You answered ${pluralize(dataPoint.answers.correct, 'question')} correctly out of ${dataPoint.answers.total}`;

    return (
      <ProgressBar
        color={barColor}
        backgroundColor={colors.grayLighter}
        percentage={isNull(dataPoint.answers.percentCorrect) ? 100 : dataPoint.answers.percentCorrect} // Hack: Improper border radius when % is 0 so use 100 w/ the same bg color
        size="lg"
        minLabel={isNull(dataPoint.answers.percentCorrect) ? 'N/A' : `${dataPoint.answers.percentCorrect}%`}
        minLabelStyle={{
          display: 'flex',
          alignSelf: 'flex-start',
          paddingLeft: '10px',
          color: labelColor
        }}
        popoverText={popOverText}
      />
    );
  };

  const groups = dataPointGroups();
  const overall = overallDataPoint();

  return (
    <div className="d-flex flex-column gap-m">
      <div className="align-items-lg-center d-flex flex-column flex-lg-row gap-s gap-lg-l">
        <div className="align-items-center d-flex gap-l">
          <h4 className={cx(style.conceptName, 'flex-1 u-margin-A-n')}><strong>Overall</strong></h4>

          <div className={cx(style.conceptPercentCorrect, 'hidden-sm hidden-xs small')}>
            {(() => {
              if (overall.answers.correct && overall.answers.total) {
                return `${overall.answers.correct}/${overall.answers.total} Correct`;
              } else if (overall.answers.total) {
                return pluralize(overall.answers.total, 'Answer');
              } else {
                return 'No Answers';
              }
            })()}
          </div>
        </div>

        <div className="flex-1">
          {renderProgressBar('overall', overall)}
        </div>

        <div className={style.conceptAction} />
      </div>

      {
        groups.map((group, idx) => {
          const FrequencyIcon = {
            high: HighSignificanceIcon,
            low: LowSignificanceIcon,
            medium: ModerateSignificanceIcon
          }[group.frequency];

          return (
            <React.Fragment key={idx}>
              <hr className="u-margin-A-n" />

              <div className="d-flex">
                <div>
                  <FrequencyIcon fill={colorForGroup(group.frequency)} height={24} width={24} />
                  {' '}<strong style={{ fontSize: 18 }}>{capitalize(group.frequency)} Frequency</strong>
                </div>
              </div>

              {
                group.concepts.map((concept) => {
                  const detailPath = paths.analyticsDetail(
                    props.section,
                    concept.slug,
                    { period: props.period }
                  );

                  return (
                    <div
                      key={concept.name}
                      className="align-items-lg-center d-flex flex-column flex-lg-row gap-s gap-lg-l"
                    >
                      <div className="align-items-center d-flex gap-l">
                        <div className="d-flex flex-1 gap-s">
                          <div className={cx(style.conceptName, 'flex-1')}>{concept.name}</div>

                          <div className="small visible-sm visible-xs">
                            <Link
                              className="text-gray-light"
                              to={detailPath}
                            >
                              Details
                            </Link>
                          </div>
                        </div>

                        <div className={cx(style.conceptPercentCorrect, 'hidden-sm hidden-xs small')}>
                          {concept.answers.total ? `${concept.answers.correct}/${concept.answers.total} Correct` : 'No Data'}
                        </div>
                      </div>

                      <div className="flex-1">
                        {renderProgressBar(group.frequency, concept)}
                      </div>

                      <div className={cx(style.conceptAction, 'hidden-sm hidden-xs small')}>
                        <Link to={detailPath}>Details</Link>
                      </div>
                    </div>
                  );
                })
              }
            </React.Fragment>
          )
        })
      }
    </div>
  );
};

export default SectionConcepts;
